import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import jobsAPI from '../api/jobs';

// Create context
const JobContext = createContext();

// Custom hook to use the job context
export const useJob = () => {
  return useContext(JobContext);
};

export const JobProvider = ({ children }) => {
  const [jobTitles, setJobTitles] = useState([]);
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreOffers, setHasMoreOffers] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Use refs to track in-flight requests and prevent duplicates
  const requestInProgressRef = useRef(false);
  const abortControllerRef = useRef(null);
  
  // Cleanup function to abort any pending requests
  const abortRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    requestInProgressRef.current = false;
  }, []);
  
  // Fetch job titles
  const fetchJobTitles = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await jobsAPI.getJobTitles();
      setJobTitles(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching job titles:', error);
      // Set empty array instead of throwing error to allow the app to continue
      setJobTitles([]);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Fetch offers with pagination
  const fetchOffers = useCallback(async (jobTitleIds = null, page = 1, perPage = 20, config = {}) => {
    try {
      setIsLoading(true);
      // Reset pagination state when fetching from the beginning
      if (page === 1) {
        setCurrentPage(1);
        setOffers([]);
      }
      
      const response = await jobsAPI.getOffers(jobTitleIds, page, perPage, config);
      
      // Check if we have more offers to load
      setHasMoreOffers(response.data.length === perPage);
      
      // Update current page
      setCurrentPage(page);
      
      // If it's the first page, replace offers, otherwise append
      if (page === 1) {
        setOffers(response.data);
      } else {
        setOffers(prevOffers => [...prevOffers, ...response.data]);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching offers:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Load more offers (for pagination)
  const loadMoreOffers = useCallback(async (jobTitleIds = null) => {
    if (!hasMoreOffers || isLoadingMore) return;
    
    try {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      await fetchOffers(jobTitleIds, nextPage, 20);
    } catch (error) {
      console.error('Error loading more offers:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, fetchOffers, hasMoreOffers, isLoadingMore]);
  
  // Fetch both job titles and offers in a single function
  const fetchInitialData = useCallback(async (jobTitleIds = null) => {
    // If a request is already in progress, don't start another one
    if (requestInProgressRef.current) {
      console.log('Request already in progress, skipping duplicate request');
      return;
    }
    
    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create a new AbortController
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    
    // Mark request as in progress
    requestInProgressRef.current = true;
    setIsLoading(true);
    
    // Reset pagination state
    setCurrentPage(1);
    setHasMoreOffers(true);
    
    try {
      // Create axios request configs with the abort signal
      const jobTitlesConfig = { signal };
      const offersConfig = { signal };
      
      // Use Promise.allSettled to fetch both job titles and offers in parallel
      // and continue even if one of them fails
      const [jobTitlesResult, offersResult] = await Promise.allSettled([
        jobsAPI.getJobTitles(jobTitlesConfig),
        jobsAPI.getOffers(jobTitleIds, 1, 20, offersConfig)
      ]);
      
      // Check if the request was aborted
      if (signal.aborted) {
        console.log('Request was aborted');
        return;
      }
      
      // Handle job titles result
      if (jobTitlesResult.status === 'fulfilled') {
        setJobTitles(jobTitlesResult.value.data);
      } else {
        if (jobTitlesResult.reason?.name !== 'CanceledError' && 
            jobTitlesResult.reason?.name !== 'AbortError') {
          console.error('Error fetching job titles:', jobTitlesResult.reason);
          setJobTitles([]);
        }
      }
      
      // Handle offers result
      if (offersResult.status === 'fulfilled') {
        const offersData = offersResult.value.data;
        setOffers(offersData);
        
        // Check if we have more offers to load
        setHasMoreOffers(offersData.length === 20);
      } else {
        if (offersResult.reason?.name !== 'CanceledError' && 
            offersResult.reason?.name !== 'AbortError') {
          console.error('Error fetching offers:', offersResult.reason);
          setOffers([]);
        }
      }
      
      return {
        jobTitles: jobTitlesResult.status === 'fulfilled' ? jobTitlesResult.value.data : [],
        offers: offersResult.status === 'fulfilled' ? offersResult.value.data : []
      };
    } catch (error) {
      // Only log errors that aren't related to cancellation
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        console.error('Error fetching initial data:', error);
      }
      return { jobTitles: [], offers: [] };
    } finally {
      // Reset request in progress flag
      requestInProgressRef.current = false;
      setIsLoading(false);
    }
  }, []);
  
  // Fetch single offer
  const fetchOffer = useCallback(async (offerId) => {
    try {
      setIsLoading(true);
      const response = await jobsAPI.getOffer(offerId);
      return response.data;
    } catch (error) {
      console.error(`Error fetching offer ${offerId}:`, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Fetch detailed position information
  const fetchPositionDetails = useCallback(async (positionId) => {
    try {
      const response = await jobsAPI.getPositionDetails(positionId);
      return response.data;
    } catch (error) {
      console.error(`Error fetching position details ${positionId}:`, error);
      throw error;
    }
  }, []);
  
  // Apply to offer
  const applyToOffer = useCallback(async (offerId) => {
    try {
      setIsLoading(true);
      const response = await jobsAPI.applyToOffer(offerId);
      
      // Update local state to reflect the application
      setOffers(prevOffers => 
        prevOffers.map(offer => 
          offer.id === offerId 
            ? { ...offer, applied: true } 
            : offer
        )
      );
      
      return response.data;
    } catch (error) {
      console.error(`Error applying to offer ${offerId}:`, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  const value = {
    jobTitles,
    offers,
    isLoading,
    isLoadingMore,
    hasMoreOffers,
    currentPage,
    fetchJobTitles,
    fetchOffers,
    fetchInitialData,
    fetchOffer,
    fetchPositionDetails,
    applyToOffer,
    loadMoreOffers,
    abortRequests
  };
  
  return (
    <JobContext.Provider value={value}>
      {children}
    </JobContext.Provider>
  );
};
